<template>
	<div class="table-overflow-hide">
		<div
			v-if="canEditDirectory"
			class="d-flex align-center justify-end mb-2"
			data-cy="main-teachers-only-row"
		>
			<v-switch
				:key="state.mainTeachersOnlySwitchKey"
				color="primary"
				class="custom-input"
				density="compact"
				hide-details
				label="Main teachers only"
				:model-value="state.mainTeachersOnly"
				:loading="state.loadingMainTeachersOnly"
				:disabled="state.loadingMainTeachersOnly || state.savingMainTeachersOnly"
				data-cy="main-teachers-only-toggle"
				@update:model-value="onMainTeachersOnlyChange"
			></v-switch>
		</div>
		<div class="directoryTable scrollable-55vh">
			<!-- Show table when not loading and no error -->
			<div v-if="!state.loading && !state.error" style="height: 100%">
				<table v-if="paginatedResults.length > 0" class="table" data-cy="classes-table">
					<thead class="sticky-header">
						<tr>
							<th class="icon-col">
								<uncheckbox
									:model-value="
										filteredData.length > 0 && filteredData.every((cls) => cls.excluded)
									"
									@update:model-value="
										(val) => filteredData.forEach((cls) => (cls.excluded = val))
									"
									:disabled="filteredData.length === 0 || !canEditDirectory"
								></uncheckbox>
							</th>
							<SortHeader
								title="Title"
								:asc="currentSort.asc"
								:sort-key="currentSort.key"
								column-sort-key="title"
								@click="() => setSort('title')"
								data-cy="classes-header-title"
								class="header-cell"
								color="#695b59"
								weight="500"
							></SortHeader>
							<SortHeader
								title="Course Code"
								:asc="currentSort.asc"
								:sort-key="currentSort.key"
								column-sort-key="course_code"
								@click="() => setSort('course_code')"
								data-cy="classes-header-course-code"
								class="header-cell"
								color="#695b59"
								weight="500"
							></SortHeader>
							<SortHeader
								:title="
									schoolStore.school?.integration === 'blackbaud'
										? 'Grade Level'
										: 'Subject'
								"
								:asc="currentSort.asc"
								:sort-key="currentSort.key"
								column-sort-key="subject"
								@click="() => setSort('subject')"
								data-cy="classes-header-subject"
								class="header-cell"
								color="#695b59"
								weight="500"
							></SortHeader>
							<SortHeader
								title="Class Code"
								:asc="currentSort.asc"
								:sort-key="currentSort.key"
								column-sort-key="class_code"
								@click="() => setSort('class_code')"
								data-cy="classes-header-class-code"
								class="header-cell"
								color="#695b59"
								weight="500"
							></SortHeader>
							<SortHeader
								title="Course Title"
								:asc="currentSort.asc"
								:sort-key="currentSort.key"
								column-sort-key="course_title"
								@click="() => setSort('course_title')"
								data-cy="classes-header-course-title"
								class="header-cell"
								color="#695b59"
								weight="500"
							></SortHeader>
							<SortHeader
								v-if="schoolStore.school?.integration === 'blackbaud'"
								title="Location"
								:asc="currentSort.asc"
								:sort-key="currentSort.key"
								column-sort-key="location"
								@click="() => setSort('location')"
								data-cy="classes-header-location"
								class="header-cell"
								color="#695b59"
								weight="500"
							></SortHeader>
							<th class="icon-col">
								<v-btn
									@click="fetchClasses"
									icon
									size="x-small"
									color="gray200"
									:loading="state.loading"
									style="max-width: 20px; max-height: 20px"
									data-cy="classes-reload-btn"
								>
									<v-icon>mdi-reload</v-icon></v-btn
								>
							</th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="row in paginatedResults"
							:key="row.class_id"
							:class="{
								'table-row': true,
								selected: editRelationModal.show && editRelationModal.cls === row,
							}"
						>
							<td>
								<uncheckbox v-model="row.excluded" :disabled="!canEditDirectory"></uncheckbox>
							</td>
							<td data-cy="table-col-title" :class="{ strikethrough: row.excluded }">
								{{ row.title }}
							</td>
							<td data-cy="table-col-course-code" :class="{ strikethrough: row.excluded }">
								{{ row.course_code }}
							</td>
							<td data-cy="table-col-subject" :class="{ strikethrough: row.excluded }">
								{{ row.subject }}
							</td>
							<td data-cy="table-col-class-code" :class="{ strikethrough: row.excluded }">
								{{ row.class_code }}
							</td>
							<td data-cy="table-col-course-title" :class="{ strikethrough: row.excluded }">
								{{ row.course_title }}
							</td>
							<td
								v-if="schoolStore.school?.integration === 'blackbaud'"
								data-cy="table-col-location"
								:class="{ strikethrough: row.excluded }"
							>
								{{ row.location }}
							</td>
							<td tabindex="1">
								<v-btn
									@click="
										editRelationModal.show = true;
										editRelationModal.cls = row;
									"
									color="gray300"
									density="compact"
									icon
									variant="text"
									:disabled="deleted || !canEditDirectory"
									data-cy="class-relations-btn"
								>
									<v-icon color="gray500">mdi-account-group</v-icon>
								</v-btn>
							</td>
						</tr>
					</tbody>
				</table>

				<!-- Empty state when no classes but not loading -->
				<div
					v-if="!paginatedResults.length"
					style="
						height: calc(100% - 60px);
						background-color: rgba(246, 247, 237, 0.5);
						border-radius: 15px;
					"
				>
					<div class="empty-state">
						<figure style="max-width: 300px">
							<IconListEmptyState />
						</figure>
						<div class="title">No Classes</div>
						<p>
							Classes can only be added through certain integrations and are not necessary
							otherwise
						</p>
					</div>
				</div>
			</div>

			<!-- Loading state -->
			<div v-else-if="state.loading" class="loading-splash">
				<LoadingState text="Loading Classes"></LoadingState>
			</div>

			<!-- Error state -->
			<div v-else class="empty-state-container">
				<div class="empty-state">
					<figure style="max-width: 300px">
						<IconListEmptyState />
					</figure>
					<div class="title">Error loading classes.</div>
					<p>The classes feature may not be available yet.</p>
					<div class="mt-3">
						<v-btn color="primary" @click="fetchClasses">Retry</v-btn>
					</div>
				</div>
			</div>
		</div>
	</div>
	<div class="table-pagination-btns">
		<pagination v-if="!state.error" v-bind="forComponent()"></pagination>
		<v-btn
			v-if="canEditDirectory && !deleted"
			:loading="state.loadingSave"
			@click="saveAction"
			data-cy="directory-classes-save-exclude-btn"
			>Exclude selected classes</v-btn
		>
		<v-btn
			v-else-if="canEditDirectory"
			:loading="state.loadingSave"
			@click="saveAction"
			data-cy="directory-classes-save-restore-btn"
			>Restore unselected classes</v-btn
		>
	</div>
	<v-dialog
		v-model="state.showMainTeachersOnlyConfirm"
		max-width="480"
		persistent
		data-cy="main-teachers-only-confirm-modal"
	>
		<v-card>
			<v-card-title>Main teachers only?</v-card-title>
			<v-card-text>
				This removes co-teacher connections for {{ state.linksAffected }} student-teacher
				links. Parents will only see each class's main teacher. You can turn it back off to
				restore them.
			</v-card-text>
			<v-card-actions>
				<v-spacer />
				<v-btn
					variant="text"
					data-cy="main-teachers-only-cancel-btn"
					:disabled="state.savingMainTeachersOnly"
					@click="cancelMainTeachersOnlyConfirm"
				>
					Cancel
				</v-btn>
				<v-btn
					color="primary"
					variant="flat"
					:loading="state.savingMainTeachersOnly"
					data-cy="main-teachers-only-confirm-btn"
					@click="confirmMainTeachersOnlyOn"
				>
					Turn on
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
	<class-relations-modal
		v-model:show="editRelationModal.show"
		:directory-entity="editRelationModal.cls"
		:entity-type="'class'"
		:entity-id="String(editRelationModal.cls.class_id)"
		:entity-name="`${editRelationModal.cls.title}`"
		:tabs="['student', 'teacher']"
		@entity-click="handleEntityClick"
	></class-relations-modal>
</template>

<script setup lang="ts">
import { reactive, computed, onMounted, watch } from "vue";
import usePaginate from "@/hooks/usePaginate";
import pagination from "@/components/pagination.vue";
import { complexSearch } from "@/utils";
import type { ClassesType } from "@/api_types/school";
import ClassRelationsModal from "@/components/school/directory/RelationshipModal.vue";
import { useAlertsStore } from "@/stores/alerts";
import LoadingState from "@/components/loadingState.vue";
import IconListEmptyState from "@/assets/icons/list-empty-state.svg";
import FetchService from "@/services/fetch.service";
import useTableSort from "@/hooks/useTableSort";
import { sort } from "@/utils/sort";
import SortHeader from "@/components/global/sortHeader.vue";
import useClasses from "@/stores/school/classes";
import uncheckbox from "@/components/global/uncheckbox.vue";
import { useAuthenticationStore } from "@/stores/authentication";
import { useSchoolStore } from "@/stores/school";

const props = defineProps<{
	tab: "teacher" | "parent" | "student" | "team" | "class";
	search: string;
	deleted: boolean;
	selectedEntity?: any;
}>();

const emit = defineEmits<{
	(e: "update:search", ...args: any[]): void;
	(e: "entity-click", entityData: any): void;
}>();

const authStore = useAuthenticationStore();
const schoolStore = useSchoolStore();
const alertsStore = useAlertsStore();

const canEditDirectory = computed(() => authStore.hasPolicy("school.directory.classes.write"));

const { currentSort, setSort } = useTableSort([
	{
		key: "title",
		title: "Title",
		sortKey: "title",
	},
	{
		key: "course_code",
		title: "Course Code",
		sortKey: "course_code",
	},
	{
		key: "subject",
		title: "Subject",
		sortKey: "subject",
	},
	{
		key: "class_code",
		title: "Class Code",
		sortKey: "class_code",
	},
	{
		key: "course_title",
		title: "Course Title",
		sortKey: "course_title",
	},
	{
		key: "location",
		title: "Location",
		sortKey: "location",
	},
]);

const state = reactive({
	classes: [] as ClassesType,
	loadingPins: [] as string[],
	loadingDownload: false,
	loading: false,
	loadingSave: false,
	error: false,
	mainTeachersOnly: false,
	linksAffected: 0,
	loadingMainTeachersOnly: false,
	savingMainTeachersOnly: false,
	showMainTeachersOnlyConfirm: false,
	mainTeachersOnlySwitchKey: 0,
});

const editRelationModal = reactive({ show: false, cls: {} as ClassesType[number] });
// const uploadModal = reactive({ show: false });
const classesStore = useClasses();
async function saveAction() {
	try {
		state.loadingSave = true;
		if (props.deleted) {
			// Restore unselected classes
			await classesStore.include(paginatedResults.value.filter((cls) => !cls.excluded));
		} else {
			// Exclude selected classes
			await classesStore.exclude();
		}
		// await classesStore.exclude();
	} catch (error) {
		console.error("Error in saveAction:", error);
	} finally {
		state.loadingSave = false;
	}
}

async function fetchClasses() {
	try {
		state.loading = true;
		state.error = false;
		await classesStore.fetch();
		state.error = !!classesStore.error;
	} catch (error) {
		console.error("Error in fetchClasses:", error);
		state.error = true;
	} finally {
		state.loading = false;
	}
}

async function fetchMainTeachersOnly() {
	try {
		state.loadingMainTeachersOnly = true;
		const res = await FetchService.fetchData("/school/main-teachers-only", "get");
		if (res && typeof res === "object") {
			state.mainTeachersOnly = Number(res.main_teachers_only) === 1;
			state.linksAffected = Number(res.links_affected) || 0;
		}
	} catch (error) {
		console.error("Error loading main-teachers-only setting:", error);
	} finally {
		state.loadingMainTeachersOnly = false;
	}
}

async function saveMainTeachersOnly(enabled: boolean) {
	try {
		state.savingMainTeachersOnly = true;
		const res = await FetchService.fetchData("/school/main-teachers-only", "post", {
			main_teachers_only: enabled ? 1 : 0,
		});
		if (res === false || res?.status === false) {
			alertsStore.addAlert("error", res?.message || "Failed to update main teachers only");
			return false;
		}
		if (res && typeof res === "object" && "main_teachers_only" in res) {
			state.mainTeachersOnly = Number(res.main_teachers_only) === 1;
			if ("links_affected" in res) {
				state.linksAffected = Number(res.links_affected) || 0;
			}
		} else {
			state.mainTeachersOnly = enabled;
		}
		state.showMainTeachersOnlyConfirm = false;
		alertsStore.addAlert(
			"success",
			enabled
				? "Main teachers only enabled"
				: "Main teachers only disabled; co-teacher links restored"
		);
		await fetchClasses();
		await fetchMainTeachersOnly();
		return true;
	} catch (error) {
		console.error("Error saving main-teachers-only setting:", error);
		alertsStore.addAlert("error", "An unexpected error occurred");
		return false;
	} finally {
		state.savingMainTeachersOnly = false;
	}
}

const resetMainTeachersOnlySwitch = () => {
	state.mainTeachersOnlySwitchKey += 1;
};

const onMainTeachersOnlyChange = (value: boolean | null) => {
	if (value) {
		state.showMainTeachersOnlyConfirm = true;
		resetMainTeachersOnlySwitch();
		return;
	}
	saveMainTeachersOnly(false);
};

const cancelMainTeachersOnlyConfirm = () => {
	state.showMainTeachersOnlyConfirm = false;
	resetMainTeachersOnlySwitch();
};

const confirmMainTeachersOnlyOn = () => {
	saveMainTeachersOnly(true);
};

onMounted(() => {
	fetchClasses();
	fetchMainTeachersOnly();
});

watch(
	() => props.tab,
	(newTab) => {
		if (newTab === "class") {
			fetchClasses();
			fetchMainTeachersOnly();
		}
	}
);

const filteredData = computed(() => {
	const searchTerm = props.search?.trim() || "";
	// const isDeleted = props.deleted;

	const filtered = (classesStore.data ?? []).filter((cls) => {
		// Only include classes matching the deleted status
		if (cls.deleted !== props.deleted) return false;

		// Build a string of searchable fields
		const haystack = [
			cls.title,
			cls.course_title,
			cls.class_code,
			cls.course_code,
			cls.location,
			cls.subject,
		]
			.filter(Boolean)
			.join(" ")
			.toLowerCase();

		// Match search term if provided
		return !searchTerm || complexSearch(searchTerm, haystack);
	});

	// Sort the filtered data based on current sort configuration
	const sortKey = currentSort.value.key as keyof ClassesType[number];
	const asc = currentSort.value.asc;
	
	
	const sorted = sort(
		filtered,
		(i) => {
			// For title sorting, use multi-level sort: title first, then class_code
			if (sortKey === "title") {
				const title = i.title?.toLowerCase() || "";
				const classCode = i.class_code?.toString() || "";
				// Create a sortable string that combines title and class_code
				// Pad class_code with zeros to ensure proper numeric sorting
				const paddedClassCode = classCode.padStart(10, '0');
				return `${title}${paddedClassCode}`;
			}
			
			// For other fields, use the original logic
			const value = i[sortKey];
			if (typeof value === "string") {
				return value.toLowerCase();
			}
			return value || "";
		},
		asc
	);
	
	
	return sorted;
});

const { paginatedResults, pageStatus, totalPages, forComponent } = usePaginate(filteredData);


// Handle entity clicks from relationship modal
const handleEntityClick = (entityData: any) => {
	emit('entity-click', entityData);
};

// Watch for selected entity changes and open the relationship modal
watch(() => props.selectedEntity, (newEntity) => {
	if (newEntity && newEntity.entityType === 'class') {
		// Find the class in the current data
		// Handle both string and number comparisons for class_id
		const cls = classesStore.data.find(c => 
			String(c.class_id) === String(newEntity.entityId) || 
			c.class_id === newEntity.entityId ||
			(c as any).uid === newEntity.entityId
		);
		if (cls) {
			editRelationModal.cls = cls;
			editRelationModal.show = true;
		}
	}
}, { immediate: true });
</script>
<style scoped lang="scss">
.strikethrough {
	text-decoration: line-through;
	color: #aaa;
}
</style>
